import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { topBarImages } from "../../../../reusableUI/images";
import secureLocalStorage from "react-secure-storage";
import { CiLogout } from "react-icons/ci";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import { LuDelete } from "react-icons/lu";
import { FaBuildingUser } from "react-icons/fa6";
import { MdLogout, MdOutlineNotifications } from "react-icons/md";
import PartnerNotification from "./partnerNotification";
import PartnerViewAllNotificationsModal from "./partnerViewAllNotificationsModal";
import moment from "moment";
import {
  getPartnerNotifications,
  markPartnerNotificationSeen,
  markAllPartnerNotificationsSeen,
} from "../../../../Service/partnerAPI";
import { logoutAuthentication } from "../../../../Service/apiURL";

const formatNotificationTime = (date) => {
  if (!date) return "";

  const parsedDate = moment(date);
  if (!parsedDate.isValid()) return "";

  if (moment().diff(parsedDate, "hours") < 24) {
    return parsedDate.fromNow();
  }

  return parsedDate.format("DD MMM, hh:mm A");
};

const normalizeNotificationStatus = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  if (normalizedStatus === "read") return "seen";
  if (normalizedStatus === "unread") return "unseen";
  return normalizedStatus || "unseen";
};

const TopBar = ({ searchTerm, setSearchTerm }) => {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [partnerName, setPartnerName] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [showViewAllNotifications, setShowViewAllNotifications] =
    useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const notificationRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const searchEnabledPaths = [
    "/admin/account",
    "/admin/visits",
    "/admin/enquirylist",
    "/admin/user",
    "/partner/careteam",
    "/partner/visits",
    "/partner/patients",
    "/partner/bookings",
    "/admin/partnerlist",
    "/admin/hospitalProfile",
  ];
  const isSearchEnabled = searchEnabledPaths.includes(location.pathname);

  useEffect(() => {
    const storedPartnerName = secureLocalStorage.getItem("partnerName");
    const isAuth = secureLocalStorage.getItem("isAuth");
    setPartnerName(storedPartnerName || "");

    if (!isAuth) {
      setSearchTerm("");
      logoutAuthentication();
    }
  }, [navigate, setSearchTerm]);

  const handleLogout = async () => {
    setSearchTerm("");
    await logoutAuthentication();
  };

  const handleClear = () => {
    setSearchTerm("");
  };

  const isPartnerUser = !!partnerName;
  const unseenCount = notifications.filter(
    (item) => normalizeNotificationStatus(item?.status) === "unseen",
  ).length;
  const notificationPreview = notifications.slice(0, 8);

  const fetchPartnerNotifications = useCallback(async () => {
    if (!isPartnerUser) return;

    setNotificationLoading(true);
    const response = await getPartnerNotifications();
    if (response?.error === false && Array.isArray(response?.data)) {
      const normalizedNotifications = response.data.map((item) => ({
        ...item,
        status: normalizeNotificationStatus(item?.status),
      }));
      setNotifications(normalizedNotifications);
      setNotificationLoading(false);
      return normalizedNotifications;
    }
    setNotificationLoading(false);
    return [];
  }, [isPartnerUser]);

  // console.log(notifications);

  useEffect(() => {
    fetchPartnerNotifications();
  }, [fetchPartnerNotifications]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchPartnerNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchPartnerNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNotificationToggle = async () => {
    setShowNotifications(true);
    await fetchPartnerNotifications();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?._id) return;

    if (normalizeNotificationStatus(notification?.status) === "unseen") {
      await markPartnerNotificationSeen(notification._id);
      setNotifications((prev) =>
        prev.map((item) =>
          item?._id === notification._id ? { ...item, status: "seen" } : item,
        ),
      );
    }

    setShowNotifications(false);

    const type = String(notification?.type || "").trim();
    const title = String(notification?.title || "").toLowerCase();

    if (type === "Visit") {
      if (
        title.includes("duty ended") ||
        title.includes("duty started") ||
        title.includes("standby specialist assigned")
      ) {
        navigate("/partner/visits");
      } else {
        navigate("/partner/bookings");
      }
    } else if (title.includes("support ticket") || type === "Support") {
      navigate("/partner/helpAdmin");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const res = await markAllPartnerNotificationsSeen();
      if (res && !res.error) {
        setNotifications((prev) =>
          prev.map((item) => ({ ...item, status: "seen" })),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mt-[-1.50rem] p-0 w-full">
        {/* Search Bar */}
        <div className="flex items-center justify-between bg-white rounded-md shadow-md p-1 h-10 my-5 mx-1 w-3/5">
          <div className="flex items-center w-full">
            <img
              src={topBarImages.search}
              className={`h-4 w-4 ml-2.5 ${!isSearchEnabled ? "opacity-50" : ""}`}
              alt="Search Icon"
            />
            <input
              type="text"
              placeholder={isSearchEnabled ? "Search" : "Search"}
              className={`border-none ml-2.5 w-full h-full focus:outline-none placeholder-medium bg-transparent ${!isSearchEnabled ? "cursor-not-allowed" : ""}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!isSearchEnabled}
              style={{
                color: !isSearchEnabled ? "gray" : "black",
                caretColor: isSearchEnabled ? "blue" : "transparent",
              }}
            />
          </div>

          <div
            onClick={isSearchEnabled ? handleClear : undefined}
            className={`mr-2 ${isSearchEnabled ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
          >
            <LuDelete size={20} color="gray" />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center mr-3 ml-0 md:ml-8 ">
          {isPartnerUser ? (
            <>
              <div className="flex items-center gap-4   px-4 py-2 h-10 ">
                <div className="flex flex-row items-center gap-2 ">
                  <div className=" p-2 w-8 h-8">
                    {" "}
                    <FaBuildingUser color={"blue"} size={16} />
                  </div>

                  <span className="text-[13px] font-medium font-poppins text-[#1f1f1f] whitespace-nowrap">
                    {capitalizeFirstLetter(partnerName)}
                  </span>
                </div>
              </div>
              <div className="flex flex-row items-center bg-[white] shadow-md rounded-lg p-2 gap-3">
                <button
                  onClick={handleNotificationToggle}
                  className="text-[#4951EC] hover:text-[#2f37d3] transition relative"
                  title="Notifications"
                >
                  <MdOutlineNotifications size={24} />
                  {unseenCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-[9px] font-bold leading-none text-white transform translate-x-1/4 -translate-y-1/4 bg-red-500 rounded-full">
                      {unseenCount > 9 ? "9+" : unseenCount}
                    </span>
                  )}
                </button>

                <div className="w-[1px] h-5 bg-gray-300"></div>

                {/* <p>Logout</p> */}
                <button
                  onClick={() => setShowLogoutModal(true)}
                  className="text-[#4951EC] hover:text-[#2f37d3] transition"
                  title="Logout"
                >
                  <MdLogout size={20} />
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-row items-center">
              <button className="bg-custom-gradient transition delay-700 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 text-white px-6 p-2 ml-1 rounded-md text-[14px] h-10">
                {capitalizeFirstLetter(
                  isPartnerUser ? partnerName : "Admin",
                )}
              </button>

              <button
                className="border border-[#4951EC] text-blue-600 px-6 p-2 ml-2 rounded-md text-[14px] h-10"
                onClick={() => setShowLogoutModal(true)}
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Logout Modal */}
      {showLogoutModal && (
        <div className="w-full fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="flex flex-col items-center bg-white rounded-lg shadow-lg p-6 py-12 w-[90%] md:max-w-[22%]">
            <div className="bg-[#e9eef0] rounded-full flex items-center justify-center p-3 mb-4">
              <CiLogout size={26} color="black" />
            </div>

            <h3 className="text-[16px] font-semibold text-center mb-10">
              Are you sure you want to log out?
            </h3>

            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="text-[#4951EC] px-6 py-2 rounded-md text-[16px]"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="bg-custom-gradient text-white px-6 py-2 rounded-md hover:bg-blue-600 transition duration-200 text-[16px]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <PartnerNotification
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onMarkAllAsRead={handleMarkAllAsRead}
        onViewAllClick={() => setShowViewAllNotifications(true)}
        loading={notificationLoading}
      />

      {/* View All Notifications Modal */}
      <PartnerViewAllNotificationsModal
        isOpen={showViewAllNotifications}
        onClose={() => setShowViewAllNotifications(false)}
      />
    </>
  );
};

export default TopBar;
